import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ErrorState, FiltersSection, LoadingState, PayoutsAgGridTable } from '../';
import { usePayouts, usePayoutsMain } from '../../hooks';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CreateAdvanceButton } from '../interaction/CreateAdvanceButton';
import { CreatePayoutButton } from '../interaction/CreatePayoutButton';
import { PayoutsHeaderActions } from '../interaction/PayoutsHeaderActions';

export const Main = () => {
  const { t } = useTranslation('codPayouts');
  const navigate = useNavigate();
  const { filters, updateFilters, resetFilters, changePage, handleSortingChange, sorting } = usePayoutsMain();

  const { payouts, meta, isLoading, isFetching, error, refetch } = usePayouts({
    filters,
    sorting,
  });

  const { columnDefs, getColumnsInOrder } = useAgGridColumns();

  // Estados para controlar los modales
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);

  // 🎯 Estado de carga inicial (solo mostrar loader completo la primera vez)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Configurar breadcrumbs
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.payouts', 'Liquidaciones COD') }], [t]);

  // Obtener columnas para exportar
  const exportColumns = useMemo(() => getColumnsInOrder(), [getColumnsInOrder]);

  // Configurar acciones del header con dropdown
  const headerActions = useMemo(
    () => [
      {
        id: 'payouts-actions',
        label: '',
        onClick: () => {},
        component: (
          <PayoutsHeaderActions
            onViewItems={() => navigate('/cod-payments/payout-items')}
            onCreatePayout={() => setIsPayoutModalOpen(true)}
            onCreateAdvances={() => setIsAdvanceModalOpen(true)}
          />
        ),
      },
    ],
    [navigate, t],
  );

  // Establecer breadcrumbs y acciones en la topbar
  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // Estado de carga inicial (solo la primera vez)
  if (isInitialLoad && isLoading) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.payouts', 'Cargando liquidaciones...')} />
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  // Total de payouts para detectar estado vacío
  const total = meta?.total ?? 0;
  const hasFilters = filters.date_from || filters.date_to || filters.client_id;

  return (
    <div className="px-6 pt-4">
      {/* Filtros */}
      <FiltersSection
        filters={filters}
        onFiltersChange={updateFilters}
        onResetFilters={resetFilters}
        exportColumns={exportColumns}
      />

      {/* 🎯 Estado vacío: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`transition-opacity duration-200 overflow-hidden ${
          total === 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
        }`}
      >
        <EmptyView
          type={hasFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={
            hasFilters
              ? t('empty.no_results_title', 'No se encontraron resultados')
              : t('empty.title', 'No hay liquidaciones')
          }
          description={
            hasFilters
              ? t(
                  'empty.no_results_description',
                  'No hay liquidaciones que coincidan con los filtros aplicados. Intenta ajustar los filtros.',
                )
              : t('empty.description', 'No se encontraron liquidaciones')
          }
        />
      </div>

      {/* 🎯 Vista de payouts: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`mb-4 transition-opacity duration-200 overflow-hidden ${
          total > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0'
        }`}
      >
        <PayoutsAgGridTable
          payouts={payouts || []}
          columnDefs={columnDefs}
          isLoading={isLoading}
          isFetching={isFetching}
          sorting={sorting}
          onSortingChange={handleSortingChange}
        />
      </div>

      {/* Paginación */}
      {meta && meta.last_page > 1 && total > 0 && (
        <div className="flex justify-between items-center mt-4 px-2">
          <div className="text-sm text-muted-foreground">
            {t('pagination.page', 'Página')} {meta.current_page} {t('pagination.of', 'de')} {meta.last_page}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => changePage(meta.current_page - 1)}
              disabled={meta.current_page === 1 || isFetching}
              className="px-3 py-1 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.previous', 'Anterior')}
            </button>
            <button
              onClick={() => changePage(meta.current_page + 1)}
              disabled={meta.current_page === meta.last_page || isFetching}
              className="px-3 py-1 text-sm border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('pagination.next', 'Siguiente')}
            </button>
          </div>
        </div>
      )}

      {/* Modales */}
      <CreatePayoutButton isOpen={isPayoutModalOpen} onClose={() => setIsPayoutModalOpen(false)} />
      <CreateAdvanceButton isOpen={isAdvanceModalOpen} onClose={() => setIsAdvanceModalOpen(false)} />
    </div>
  );
};
